<script lang="ts">
  import { getContext, onMount } from 'svelte';
  import { slide } from 'svelte/transition';
  import type { AccordionContext } from './Accordion.svelte';

  import OpenClose from '../OpenClose.svelte';
  import Typography from '../Typography.svelte';

  export let title: string;
  export let open: boolean = false;

  const { selected } = getContext<AccordionContext>('accordion');

  let item: HTMLDivElement;

  let initialOpen = open;

  $: {
    open = $$slots.default && $selected === item;
  }

  onMount((): void => {
    if (initialOpen) {
      selected.set(item);
    }
  });

  function handleToggle(): void {
    selected.set(!open ? item : null);
  }
</script>

<div bind:this={item} class="accordion-item" class:open>
  <button on:click={handleToggle} type="button" class="accordion-item-header" aria-expanded={open}>
    <div class="accordion-item-header-text">
      <span class="icon"><OpenClose {open} /></span>
      <Typography type="span" display>{title}</Typography>
    </div>
  </button>
  {#if open}
    <div transition:slide class="accordion-item-content">
      <slot />
    </div>
  {/if}
</div>

<style>
  :root {
    --accordion-item-height: calc(var(--accordion-height, var(--accordion-height-fallback)) * 0.08);
    /** accordion content height set to 100% - 4 accordion item headers */
    --accordion-content-height: calc(var(--accordion-height, var(--accordion-height-fallback)) * 0.68);
  }

  .accordion-item {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .accordion-item-header {
    appearance: none;
    border: none;
    outline: none;
    background: none;
    cursor: pointer;

    display: flex;
    flex-direction: column;

    width: 100%;
    max-width: var(--centre-width);
    padding-inline: 1.8rem;

    text-transform: uppercase;
  }

  .accordion-item-header-text {
    display: flex;
    align-items: center;
    width: 100%;
    height: var(--accordion-item-height);
    gap: var(--layout-side-padding);
  }

  .icon {
    border-radius: 100%;
  }

  button:focus .icon {
    box-shadow: 0 0 0 var(--outline) var(--active);
    border: none;

    /* High-contrast */
    outline-color: transparent;
    outline-width: var(--outline-width);
    outline-style: dotted;
  }

  .accordion-item-content {
    width: 100%;
    height: var(--accordion-content-height);
  }
</style>
